import React, { useEffect } from "react";
import { format } from "date-fns";
import { Navigate } from "react-router-dom";

import { OURS_EVENTS, trackOursEvent } from "~/providers";
import { ROUTES } from "~/router";
import { useBookingFlowEvents } from "~/services";
import { usePageTitle } from "~/shared";
import { TimeSlot } from "~/shared.types";
import {
  AppointmentDetails,
  useAuthStore,
  useBookingFlowStore,
  usePatientStore,
  useUserStore,
} from "~/stores";
import { getVisitTypeTitle, useRedirect } from "~/utils";
import { ScheduleVisit } from "./components";
import { useScheduleVisit } from "./hooks";
import { getVisitNavigation } from "./utils";

export const InitialVisit = () => {
  const redirectTo = useRedirect();
  const stateId = useUserStore((state) => state.state);
  const isLoggedIn = useAuthStore((state) => state.isLoggedIn());
  const isNirvanaEnabled = useBookingFlowStore((s) => s.isNirvanaEnabled);
  const {
    visitType,
    updatePatientVisitData,
    setAppointmentDetails,
    selectedProvider: providerId,
    insuranceData,
  } = usePatientStore();
  usePageTitle(getVisitTypeTitle(visitType));
  const { trackSelectedSlot, trackSchedulingEntryPage } =
    useBookingFlowEvents();

  const onContinue = (details: AppointmentDetails) => {
    if (!details.timeSlot) return;

    updatePatientVisitData({
      visitDateTime: details.visitDateTime,
      appointmentType: visitType,
      providerId: details.provider?.id,
      patientStateId: stateId || undefined,
    });
    trackSelectedSlot(
      details.timeSlot,
      details.formattedDate,
      details.provider?.id,
    );

    setAppointmentDetails(details);
    if (isLoggedIn) {
      redirectTo(ROUTES.hubMeetProviderPatientInformation);
    } else {
      redirectTo(ROUTES.schedulingEmailInput);
    }
  };

  const {
    calendar,
    selection,
    calendarHandlers,
    selectionHandlers,
    isPendingProviderView,
    isPendingFirstAvailableView,
    appointmentSlotsByProvider,
    appointmentSlotsByFirstAvailable,
    handleContinue,
  } = useScheduleVisit({
    visitType,
    stateId,
    providerId: providerId,
    insuranceId: insuranceData?.id,
    isProviderView: true,
    onContinue,
  });

  useEffect(() => {
    trackSchedulingEntryPage();
  }, []);

  const handleTimeSlotSelect = (timeSlot: TimeSlot, providerId?: number) => {
    selectionHandlers.onTimeSlotSelect(timeSlot, providerId);

    if (calendar.selectedDay) {
      const appointmentDate = format(calendar.selectedDay.date, "MM/dd/yyyy");
      trackOursEvent(OURS_EVENTS.APPOINTMENT_SLOT_SELECTION, {
        appointment_date: appointmentDate,
        appointment_time: timeSlot.startTime,
        appointment_type: visitType,
      });
    }
  };

  const bypassesNirvanaCheck = !!insuranceData?.bypasses_nirvana_check;

  const { route } = getVisitNavigation({
    visitType,
    isLoggedIn,
    isNirvanaEnabled,
    bypassesNirvanaCheck,
  });
  const handleGoBack = () => {
    redirectTo(route);
  };

  if (!stateId) {
    return <Navigate to={ROUTES.getStarted} />;
  }

  return (
    <ScheduleVisit
      visitType={visitType}
      isProviderView={true}
      handleGoBack={handleGoBack}
      calendar={calendar}
      selection={selection}
      calendarHandlers={calendarHandlers}
      selectionHandlers={{
        ...selectionHandlers,
        onTimeSlotSelect: handleTimeSlotSelect,
      }}
      appointmentSlotsByProvider={appointmentSlotsByProvider}
      appointmentSlotsByFirstAvailable={appointmentSlotsByFirstAvailable}
      isPendingProviderView={isPendingProviderView}
      isPendingFirstAvailableView={isPendingFirstAvailableView}
      onContinue={handleContinue}
    />
  );
};
